// src/utils/offlineOrderPdfExporter.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate, formatDateTime } from './dateUtils';
import { getImageUrl } from './imageUtils';

const safeNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

export const formatCurrency = (amount) => `LKR ${safeNum(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Shared theme + drawing primitives (factored out of the original exportOfflineOrderPDF) ──
const hexToRgb = (hex) => {
  const clean = (hex || '').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const bigint = parseInt(full, 16);
  if (full.length !== 6 || isNaN(bigint)) return [79, 70, 229];
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};
const lighten = (rgb, amount = 0.94) => rgb.map((c) => Math.round(c + (255 - c) * amount));

// Builds the RGB theme object each drawing helper expects, from the business's active
// theme colors (hex, read live via src/utils/themeColors.js's getCssVar). Falls back to the
// original indigo values when no theme colors are passed — non-breaking for any caller.
export const buildTheme = ({ primary = '#4f46e5', primaryDark = '#3730a3' } = {}) => {
  const primaryRgb = hexToRgb(primary);
  return { primary: primaryRgb, primaryDark: hexToRgb(primaryDark), bgLight: lighten(primaryRgb) };
};

// Fetches an image URL and converts it to a base64 data URL for jsPDF's addImage(). Returns
// null on any failure so callers can render the banner without a logo instead of throwing.
export const urlToDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const DARK = [17, 24, 39];
const MEDIUM = [75, 85, 99];
const MUTED = [156, 163, 175];
const BORDER = [229, 231, 235];
const WHITE = [255, 255, 255];

export const createDoc = () => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ml = 14;
  const mr = 14;
  const cw = pageW - ml - mr;
  return { doc, pageW, pageH, ml, mr, cw };
};

const setColor = (doc, rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
const setFill = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
const setDraw = (doc, rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

// Top banner: title + optional subtitle line + optional badge + date, in the theme color.
export const drawBanner = (doc, { pageW, ml, mr, theme, title, metaLine, badgeLabel, dateText, logoDataUrl }) => {
  setFill(doc, theme.primary);
  doc.rect(0, 0, pageW, 38, 'F');
  setFill(doc, theme.primaryDark);
  doc.rect(0, 34, pageW, 4, 'F');

  let textX = ml;
  if (logoDataUrl) {
    const format = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
    try {
      doc.addImage(logoDataUrl, format, ml, 7, 20, 20);
      textX = ml + 24;
    } catch {
      textX = ml;
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, textX, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(metaLine, textX, 25);

  if (badgeLabel) {
    const badgeW = 44;
    const badgeX = pageW - mr - badgeW;
    const badgeY = 11;
    setFill(doc, WHITE);
    doc.roundedRect(badgeX, badgeY, badgeW, 10, 2, 2, 'F');
    doc.setTextColor(theme.primary[0], theme.primary[1], theme.primary[2]);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(badgeLabel, badgeX + badgeW / 2, badgeY + 6.5, { align: 'center' });
  }

  if (dateText) {
    doc.setTextColor(230, 230, 250);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(dateText, pageW - mr, 28, { align: 'right' });
  }
};

// A row of N equal-width labeled columns inside a bordered strip.
const drawMetaStrip = (doc, { y, ml, cw, columns }) => {
  const colW = cw / columns.length;
  setFill(doc, [249, 250, 251]);
  setDraw(doc, BORDER);
  doc.rect(ml, y, cw, 20, 'FD');
  columns.forEach((_, i) => {
    if (i === 0) return;
    doc.line(ml + colW * i, y, ml + colW * i, y + 20);
  });
  columns.forEach(({ label, value }, i) => {
    const x = ml + colW * i;
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    setColor(doc, MUTED);
    doc.text(label.toUpperCase(), x + 5, y + 6);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    setColor(doc, DARK);
    doc.text(value || '—', x + 5, y + 14);
  });
  return y + 26;
};

// Two side-by-side header+body cards (e.g. Customer / Summary).
export const drawTwoColCards = (doc, { y, ml, cw, theme, left, right }) => {
  const halfW = (cw - 6) / 2;
  const drawCard = (x, card) => {
    setFill(doc, theme.primary);
    doc.rect(x, y, halfW, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text(card.title.toUpperCase(), x + 4, y + 5);

    const bodyH = 7 * card.lines.length + 8;
    setFill(doc, WHITE);
    setDraw(doc, BORDER);
    doc.rect(x, y + 7, halfW, bodyH, 'FD');

    let ly = y + 13;
    card.lines.forEach((line, i) => {
      doc.setFontSize(i === 0 ? 9 : 8);
      doc.setFont('helvetica', i === 0 ? 'bold' : 'normal');
      setColor(doc, i === 0 ? DARK : MEDIUM);
      doc.text(line, x + 4, ly);
      ly += i === 0 ? 5 : 5;
    });
    return bodyH;
  };
  const leftH = drawCard(ml, left);
  const rightH = drawCard(ml + halfW + 6, right);
  return y + 7 + Math.max(leftH, rightH) + 8;
};

// Items table via autoTable with the standard footer (document label + page number).
export const drawItemsTable = (doc, { startY, ml, mr, pageW, pageH, theme, head, body, footerLabel }) => {
  autoTable(doc, {
    startY,
    margin: { left: ml, right: mr },
    head: [head],
    body,
    theme: 'plain',
    styles: {
      fontSize: 8,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
      textColor: DARK,
      lineColor: BORDER,
      lineWidth: 0.25,
      valign: 'middle',
    },
    headStyles: {
      fillColor: theme.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: theme.bgLight },
    didDrawPage: (data) => {
      doc.setFontSize(7);
      setColor(doc, MUTED);
      doc.text(footerLabel, ml, pageH - 7);
      doc.text(`Page ${data.pageNumber}`, pageW - mr, pageH - 7, { align: 'right' });
      setDraw(doc, BORDER);
      doc.line(ml, pageH - 10, pageW - mr, pageH - 10);
    },
  });
  return doc.lastAutoTable.finalY + 6;
};

// Right-aligned totals box (line items + a highlighted grand-total row), optionally with a
// left-aligned notes box of matching top-Y.
export const drawTotalsAndNotes = (doc, { y, ml, pageW, mr, cw, theme, totalsData, grandLabel, grandValue, notesLabel, notesText }) => {
  const sumW = 80;
  const sumX = pageW - mr - sumW;

  let fy = y;
  totalsData.forEach(({ label, value }) => {
    setFill(doc, [249, 250, 251]);
    setDraw(doc, BORDER);
    doc.rect(sumX, fy, sumW, 7, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    setColor(doc, MEDIUM);
    doc.text(label, sumX + 4, fy + 5);
    doc.text(value, sumX + sumW - 4, fy + 5, { align: 'right' });
    fy += 7;
  });

  setFill(doc, theme.primary);
  doc.rect(sumX, fy, sumW, 10, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(grandLabel, sumX + 4, fy + 6.5);
  doc.text(grandValue, sumX + sumW - 4, fy + 6.5, { align: 'right' });

  if (notesText) {
    const noteW = cw - sumW - 8;
    setFill(doc, [249, 250, 251]);
    setDraw(doc, BORDER);
    doc.rect(ml, y, noteW, 20, 'FD');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    setColor(doc, MUTED);
    doc.text(notesLabel.toUpperCase(), ml + 4, y + 5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    setColor(doc, MEDIUM);
    doc.text(notesText, ml + 4, y + 11, { maxWidth: noteW - 8 });
  }
};

// ── Offline Sales Order (unchanged behavior, now built on the shared helpers) ──
export const exportOfflineOrderPDF = async (order, themeColors) => {
  if (!order) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);
  const logoDataUrl = await urlToDataUrl(getImageUrl(order.Shop?.logo));

  drawBanner(doc, {
    pageW, ml, mr, theme, logoDataUrl,
    title: 'OFFLINE SALES INVOICE',
    metaLine: `Invoice / Order #: ${order.orderNumber || '—'}`,
    badgeLabel: (order.OrderStatus?.label || order.orderStatusKey || 'COMPLETED').toUpperCase(),
    dateText: `Date: ${formatDateTime(order.createdAt)}`,
  });

  let y = drawMetaStrip(doc, {
    y: 46, ml, cw,
    columns: [
      { label: 'Shop / Store', value: order.Shop?.name || 'Main Shop' },
      { label: 'Cashier', value: order.cashier?.name || '—' },
      { label: 'Fulfillment', value: (order.fulfillmentMethod || 'counter_pickup').replace('_', ' ').toUpperCase() },
    ],
  });

  y = drawTwoColCards(doc, {
    y, ml, cw, theme,
    left: {
      title: 'Customer Information',
      lines: [
        order.customer?.name || order.customerName || 'Cash Sales Customer',
        `Phone: ${order.customer?.phone || '—'}`,
        `Email: ${order.customer?.email || '—'}`,
        ...(order.customer?.tinNumber ? [`TIN: ${order.customer.tinNumber}`] : []),
        ...(order.customer?.vatNumber ? [`VAT: ${order.customer.vatNumber}`] : []),
      ],
    },
    right: {
      title: 'Payment Summary',
      lines: [
        `Payment Status: ${(order.paymentStatus || 'unpaid').toUpperCase()}`,
        `Paid: ${formatCurrency(order.paidAmount)}`,
        `Balance Due: ${formatCurrency(order.balanceAmount)}`,
      ],
    },
  });

  const tableItems = (order.orderItems || []).map((item, idx) => {
    const pName = item.Product?.name || item.name || 'Item';
    const sku = item.ProductVariation?.sku ? ` (${item.ProductVariation.sku})` : '';
    const free = item.freeQuantity > 0 ? `\n${item.freeQuantity} free item(s) included` : '';
    return [
      String(idx + 1),
      `${pName}${sku}${free}`,
      formatCurrency(item.unitPrice),
      String(item.quantity),
      formatCurrency(item.lineDiscount || 0),
      formatCurrency(item.lineTotal || item.quantity * item.unitPrice),
    ];
  });

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Item Description', 'Unit Price', 'Qty', 'Discount', 'Line Total'],
    body: tableItems,
    footerLabel: `Offline Sales Order — #${order.orderNumber}`,
  });

  const totalsData = [{ label: 'Subtotal', value: formatCurrency(order.totalAmount || order.grandTotal) }];
  if (safeNum(order.totalDiscount) > 0) totalsData.push({ label: 'Item Discount', value: `- ${formatCurrency(order.totalDiscount)}` });
  if (safeNum(order.orderDiscount) > 0) totalsData.push({ label: 'Order Discount', value: `- ${formatCurrency(order.orderDiscount)}` });
  if (safeNum(order.loyaltyDiscountAmount) > 0) totalsData.push({ label: 'Loyalty Discount', value: `- ${formatCurrency(order.loyaltyDiscountAmount)}` });
  if (safeNum(order.totalTax) > 0) totalsData.push({ label: 'Tax', value: `+ ${formatCurrency(order.totalTax)}` });
  if (safeNum(order.deliveryCharge) > 0) totalsData.push({ label: 'Delivery Charge', value: `+ ${formatCurrency(order.deliveryCharge)}` });

  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme, totalsData,
    grandLabel: 'Grand Total', grandValue: formatCurrency(order.grandTotal),
    notesLabel: 'Special Notes', notesText: order.specialNotes || order.notes,
  });

  doc.save(`Offline_Order_${order.orderNumber || 'Invoice'}.pdf`);
};

// ── Quotation ──
export const exportQuotationPDF = async (quotation, themeColors) => {
  if (!quotation) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);
  const logoDataUrl = await urlToDataUrl(getImageUrl(quotation.Shop?.logo));
  const itemsTotal = (quotation.items || []).reduce((sum, item) => sum + safeNum(item.lineTotal), 0);

  drawBanner(doc, {
    pageW, ml, mr, theme, logoDataUrl,
    title: 'OFFICIAL QUOTATION',
    metaLine: `Quotation #: ${quotation.quotationNumber || '—'}`,
    badgeLabel: (quotation.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(quotation.createdAt)}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Customer',
      lines: [
        quotation.customer?.name || quotation.prospectName || 'Walk-in Customer',
        `Phone: ${quotation.customer?.phone || quotation.prospectPhone || '—'}`,
      ],
    },
    right: {
      title: 'Quotation Details',
      lines: [
        `Shop: ${quotation.Shop?.name || '—'}`,
        `Valid Until: ${quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString() : '—'}`,
      ],
    },
  });

  const tableItems = (quotation.items || []).map((item, idx) => [
    String(idx + 1),
    item.Product?.name || item.displayName || 'Item',
    formatCurrency(item.unitPrice),
    String(item.quantity),
    formatCurrency(item.lineDiscount || 0),
    formatCurrency(item.lineTotal),
  ]);

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Item Description', 'Unit Price', 'Qty', 'Discount', 'Line Total'],
    body: tableItems,
    footerLabel: `Quotation — #${quotation.quotationNumber}`,
  });

  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme, totalsData: [],
    grandLabel: 'Total', grandValue: formatCurrency(itemsTotal),
    notesLabel: 'Notes', notesText: quotation.notes,
  });

  doc.save(`Quotation_${quotation.quotationNumber || 'Doc'}.pdf`);
};

// ── Return Note ──
export const exportReturnNotePDF = async (returnNote, themeColors) => {
  if (!returnNote) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);
  const logoDataUrl = await urlToDataUrl(getImageUrl(returnNote.originalOrder?.Shop?.logo));

  drawBanner(doc, {
    pageW, ml, mr, theme, logoDataUrl,
    title: 'RETURN NOTE',
    metaLine: `Return Note #: ${returnNote.returnNumber || '—'}`,
    badgeLabel: (returnNote.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(returnNote.createdAt)}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Return Details',
      lines: [
        `Original Order: #${returnNote.originalOrder?.orderNumber || '—'}`,
        `Refund Method: ${(returnNote.refundMethod || '—').toUpperCase()}`,
      ],
    },
    right: {
      title: 'Processed By',
      lines: [
        returnNote.cashier?.name || '—',
        `Total Refund: ${formatCurrency(returnNote.totalRefundAmount)}`,
      ],
    },
  });

  const tableItems = (returnNote.returnItems || []).map((item, idx) => [
    String(idx + 1),
    `${item.Product?.name || 'Item'}${item.ProductVariation?.sku ? ` (${item.ProductVariation.sku})` : ''}`,
    String(item.quantityReturned || 1),
    formatCurrency(item.refundAmount || 0),
  ]);

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Returned Item', 'Qty', 'Refund Amount'],
    body: tableItems,
    footerLabel: `Return Note — #${returnNote.returnNumber}`,
  });

  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme, totalsData: [],
    grandLabel: 'Total Refund', grandValue: formatCurrency(returnNote.totalRefundAmount),
    notesLabel: 'Reason', notesText: returnNote.reason,
  });

  doc.save(`Return_Note_${returnNote.returnNumber || returnNote.id || 'Doc'}.pdf`);
};

// ── Delivery Note ──
export const exportDeliveryNotePDF = async (deliveryNote, itemNames = {}, themeColors) => {
  if (!deliveryNote) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);
  const logoDataUrl = await urlToDataUrl(getImageUrl(deliveryNote.order?.Shop?.logo));

  drawBanner(doc, {
    pageW, ml, mr, theme, logoDataUrl,
    title: 'DELIVERY NOTE',
    metaLine: `Delivery Note #: ${deliveryNote.deliveryNoteNumber || '—'}`,
    badgeLabel: (deliveryNote.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(deliveryNote.createdAt)}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Delivery Details',
      lines: [
        `Original Order: #${deliveryNote.order?.orderNumber || '—'}`,
        `Delivered At: ${deliveryNote.deliveredAt ? formatDateTime(deliveryNote.deliveredAt) : '—'}`,
      ],
    },
    right: {
      title: 'Received By',
      lines: [
        deliveryNote.receivedByName || '—',
      ],
    },
  });

  const tableItems = (deliveryNote.items || []).map((item, idx) => [
    String(idx + 1),
    itemNames[item.orderItemId]?.name || `Item #${item.orderItemId}`,
    String(item.quantityDelivered || 1),
  ]);

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Delivered Item', 'Qty Delivered'],
    body: tableItems,
    footerLabel: `Delivery Note — #${deliveryNote.deliveryNoteNumber}`,
  });

  if (deliveryNote.notes) {
    drawTotalsAndNotes(doc, {
      y, ml, pageW, mr, cw, theme, totalsData: [],
      grandLabel: 'Items Delivered', grandValue: String((deliveryNote.items || []).length),
      notesLabel: 'Notes', notesText: deliveryNote.notes,
    });
  }

  doc.save(`Delivery_Note_${deliveryNote.deliveryNoteNumber || deliveryNote.id || 'Doc'}.pdf`);
};

// ── Goods Received Note (procurement) ──
export const exportGoodsReceivedNotePDF = async (grn, purchaseOrder, themeColors) => {
  if (!grn) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'GOODS RECEIVED NOTE',
    metaLine: `GRN #: ${grn.grnNumber || '—'}`,
    badgeLabel: (grn.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(grn.createdAt)}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Receipt Details',
      lines: [
        `Purchase Order: #${grn.purchaseOrder?.purchaseOrderNumber || '—'}`,
        `Received At: ${grn.receivedAt ? formatDateTime(grn.receivedAt) : '—'}`,
        ...(grn.supplierDeliveryReference ? [`Supplier Delivery Ref: ${grn.supplierDeliveryReference}`] : []),
      ],
    },
    right: {
      title: 'Supplier / Warehouse',
      lines: [
        purchaseOrder?.Supplier?.name || '—',
        grn.Warehouse?.name || '—',
      ],
    },
  });

  const tableItems = (grn.items || []).map((item, idx) => {
    const product = item.purchaseOrderItem?.Product;
    const variation = item.purchaseOrderItem?.ProductVariation;
    const name = product ? `${product.name}${variation?.sku ? ` (${variation.sku})` : ''}` : `Item #${item.purchaseOrderItemId}`;
    return [String(idx + 1), name, String(item.quantityReceived || 1)];
  });

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Received Item', 'Qty Received'],
    body: tableItems,
    footerLabel: `Goods Received Note — #${grn.grnNumber}`,
  });

  if (grn.notes) {
    drawTotalsAndNotes(doc, {
      y, ml, pageW, mr, cw, theme, totalsData: [],
      grandLabel: 'Items Received', grandValue: String((grn.items || []).length),
      notesLabel: 'Notes', notesText: grn.notes,
    });
  }

  doc.save(`Goods_Received_Note_${grn.grnNumber || grn.id || 'Doc'}.pdf`);
};

// ── Return To Vendor (has line items, mirrors Goods Received Note's shape) ──
export const exportReturnToVendorPDF = async (rtv, themeColors) => {
  if (!rtv) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'RETURN TO VENDOR',
    metaLine: `RTV #: ${rtv.rtvNumber || '—'}`,
    badgeLabel: (rtv.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(rtv.createdAt)}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Return Details',
      lines: [
        `Purchase Order: #${rtv.purchaseOrder?.purchaseOrderNumber || '—'}`,
        `GRN: #${rtv.goodsReceivedNote?.grnNumber || '—'}`,
        ...((rtv.debitNotes || []).length ? [`Debit Note: ${rtv.debitNotes.map((d) => d.debitNoteNumber).join(', ')}`] : []),
      ],
    },
    right: {
      title: 'Supplier',
      lines: [rtv.supplier?.name || rtv.Supplier?.name || '—'],
    },
  });

  const tableItems = (rtv.items || []).map((item, idx) => {
    const product = item.Product;
    const variation = item.ProductVariation;
    const name = product ? `${product.name}${variation?.sku ? ` (${variation.sku})` : ''}` : `Item #${item.grnItemId}`;
    return [String(idx + 1), name, String(item.quantityReturned), formatCurrency(item.unitPrice), formatCurrency(item.lineAmount)];
  });

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Item', 'Qty Returned', 'Unit Price', 'Line Amount'],
    body: tableItems,
    footerLabel: `Return To Vendor — #${rtv.rtvNumber}`,
  });

  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme, totalsData: [],
    grandLabel: 'Total Debit Amount', grandValue: formatCurrency(rtv.totalDebitAmount),
    notesLabel: 'Reason', notesText: rtv.reason || '',
  });

  doc.save(`Return_To_Vendor_${rtv.rtvNumber || rtv.id || 'Doc'}.pdf`);
};

// ── Debit Note (net new — no line items, pure financial record, mirrors Credit Note) ──
export const exportDebitNotePDF = async (debitNote, themeColors) => {
  if (!debitNote) return;
  const { doc, pageW, mr, ml, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'DEBIT NOTE',
    metaLine: `Debit Note #: ${debitNote.debitNoteNumber || '—'}`,
    badgeLabel: (debitNote.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(debitNote.createdAt)}`,
  });

  const y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Supplier / Order',
      lines: [
        debitNote.Supplier?.name || '—',
        `Purchase Order: #${debitNote.purchaseOrder?.purchaseOrderNumber || '—'}`,
        ...(debitNote.issuedBy?.name ? [`Created By: ${debitNote.issuedBy.name}`] : []),
      ],
    },
    right: {
      title: 'Source',
      lines: [(debitNote.sourceType || '—').toUpperCase()],
    },
  });

  const remaining = safeNum(debitNote.amount) - safeNum(debitNote.appliedAmount);
  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme,
    totalsData: [
      { label: 'Debit Amount', value: formatCurrency(debitNote.amount) },
      { label: 'Applied Amount', value: formatCurrency(debitNote.appliedAmount) },
    ],
    grandLabel: 'Remaining Balance', grandValue: formatCurrency(remaining),
    notesLabel: 'Reason', notesText: debitNote.reason,
  });

  doc.save(`Debit_Note_${debitNote.debitNoteNumber || debitNote.id || 'Doc'}.pdf`);
};

// ── Credit Note (net new — no line items, pure financial record) ──
export const exportCreditNotePDF = async (creditNote, themeColors) => {
  if (!creditNote) return;
  const { doc, pageW, mr, ml, cw } = createDoc();
  const theme = buildTheme(themeColors);
  const logoDataUrl = await urlToDataUrl(getImageUrl(creditNote.originalOrder?.Shop?.logo));

  drawBanner(doc, {
    pageW, ml, mr, theme, logoDataUrl,
    title: 'CREDIT NOTE',
    metaLine: `Credit Note #: ${creditNote.creditNoteNumber || '—'}`,
    badgeLabel: (creditNote.status || '').toUpperCase(),
    dateText: `Date: ${formatDateTime(creditNote.createdAt)}`,
  });

  const y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Customer',
      lines: [
        creditNote.customer?.name || 'Walk-in Customer',
        `Original Order: #${creditNote.originalOrder?.orderNumber || '—'}`,
      ],
    },
    right: {
      title: 'Source',
      lines: [
        (creditNote.sourceType || '—').toUpperCase(),
      ],
    },
  });

  const remaining = safeNum(creditNote.amount) - safeNum(creditNote.appliedAmount);
  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme,
    totalsData: [
      { label: 'Credit Amount', value: formatCurrency(creditNote.amount) },
      { label: 'Applied Amount', value: formatCurrency(creditNote.appliedAmount) },
    ],
    grandLabel: 'Remaining Balance', grandValue: formatCurrency(remaining),
    notesLabel: 'Reason', notesText: creditNote.reason,
  });

  doc.save(`Credit_Note_${creditNote.creditNoteNumber || creditNote.id || 'Doc'}.pdf`);
};

// ── Customer Statement (ledger — no single "order", a multi-row transaction table) ──
const STATEMENT_TYPE_LABELS = {
  invoice: 'Invoice',
  payment: 'Payment Received',
  credit_note: 'Credit Note',
  return: 'Refund (Cash)',
};

export const exportCustomerStatementPDF = async (statement, themeColors) => {
  if (!statement) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'CUSTOMER STATEMENT',
    metaLine: `Customer: ${statement.customer?.name || '—'}`,
    dateText: `${statement.dateFrom ? formatDate(statement.dateFrom) : 'Account start'} — ${statement.dateTo ? formatDate(statement.dateTo) : 'Today'}`,
  });

  const y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Customer',
      lines: [
        statement.customer?.name || '—',
        `Phone: ${statement.customer?.phone || '—'}`,
        ...(statement.customer?.tinNumber ? [`TIN: ${statement.customer.tinNumber}`] : []),
        ...(statement.customer?.vatNumber ? [`VAT: ${statement.customer.vatNumber}`] : []),
      ],
    },
    right: {
      title: 'Period Summary',
      lines: [
        `Opening Balance: ${formatCurrency(statement.openingBalance)}`,
        `Closing Balance: ${formatCurrency(statement.closingBalance)}`,
      ],
    },
  });

  const tableRows = (statement.transactions || []).map((t) => [
    new Date(t.date).toLocaleDateString(),
    STATEMENT_TYPE_LABELS[t.type] || t.type,
    t.reference || '—',
    t.debit > 0 ? formatCurrency(t.debit) : '—',
    t.credit > 0 ? formatCurrency(t.credit) : '—',
    formatCurrency(t.runningBalance),
  ]);

  const endY = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['Date', 'Type', 'Reference', 'Debit', 'Credit', 'Balance'],
    body: tableRows,
    footerLabel: `Customer Statement — ${statement.customer?.name || ''}`,
  });

  drawTotalsAndNotes(doc, {
    y: endY, ml, pageW, mr, cw, theme,
    totalsData: [{ label: 'Opening Balance', value: formatCurrency(statement.openingBalance) }],
    grandLabel: 'Closing Balance', grandValue: formatCurrency(statement.closingBalance),
    notesLabel: '', notesText: '',
  });

  doc.save(`Customer_Statement_${statement.customer?.name || 'Customer'}.pdf`);
};

// ── Supplier Statement (procurement's mirror of Customer Statement) ──
const SUPPLIER_STATEMENT_TYPE_LABELS = {
  purchase_order: 'Purchase Order',
  supplier_payment: 'Payment',
  debit_note: 'Debit Note',
};

export const exportSupplierStatementPDF = async (statement, themeColors) => {
  if (!statement) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'SUPPLIER STATEMENT',
    metaLine: `Supplier: ${statement.supplier?.name || '—'}`,
    dateText: `${statement.dateFrom ? formatDate(statement.dateFrom) : 'Account start'} — ${statement.dateTo ? formatDate(statement.dateTo) : 'Today'}`,
  });

  const y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Supplier',
      lines: [
        statement.supplier?.name || '—',
        `Phone: ${statement.supplier?.phone || '—'}`,
      ],
    },
    right: {
      title: 'Period Summary',
      lines: [
        `Opening Balance: ${formatCurrency(statement.openingBalance)}`,
        `Closing Balance: ${formatCurrency(statement.closingBalance)}`,
      ],
    },
  });

  const tableRows = (statement.transactions || []).map((t) => [
    new Date(t.date).toLocaleDateString(),
    SUPPLIER_STATEMENT_TYPE_LABELS[t.type] || t.type,
    t.reference || '—',
    (t.debit > 0 || t.type === 'purchase_order') ? formatCurrency(t.debit) : '—',
    t.credit > 0 ? formatCurrency(t.credit) : '—',
    formatCurrency(t.runningBalance),
  ]);

  const endY = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['Date', 'Type', 'Reference', 'Debit', 'Credit', 'Balance'],
    body: tableRows,
    footerLabel: `Supplier Statement — ${statement.supplier?.name || ''}`,
  });

  drawTotalsAndNotes(doc, {
    y: endY, ml, pageW, mr, cw, theme,
    totalsData: [{ label: 'Opening Balance', value: formatCurrency(statement.openingBalance) }],
    grandLabel: 'Closing Balance', grandValue: formatCurrency(statement.closingBalance),
    notesLabel: '', notesText: '',
  });

  doc.save(`Supplier_Statement_${statement.supplier?.name || 'Supplier'}.pdf`);
};
